import { useCallback } from 'react';

import {
  DEFAULT_DOCUMENT_NAME,
  isBlogPostProjectType,
  isDocumentProjectType,
  isLongFormProjectType,
  PROJECT_STATUS,
} from 'src/data/project';
import { useGoBack } from 'src/lib/hooks/useGoBack';
import { blogFactsAll, blogFactsCursor, blogFactsSelected } from 'src/lib/localData';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useProjectActions } from 'src/store';

import { PageStore, usePageActions } from '../store';

import { useDeleteProject } from './useDeleteProject';
import { useUpdateProject } from './useUpdateProject';

const usePageState = createStateSelectorHook(PageStore, ['projectId', 'projectType']);

export const useOnProjectPageBackClick = () => {
  const projectActions = useProjectActions();
  const pageActions = usePageActions();
  const { projectId, projectType } = usePageState();

  const deleteProject = useDeleteProject();
  const goBack = useGoBack();

  const clearFactsStorage = () => {
    blogFactsAll.remove(projectId);
    blogFactsSelected.remove(projectId);
    blogFactsCursor.remove(projectId);
  };

  const clearTitleAndGoBack = useCallback(() => {
    // Clear the project's title only when it is about to be closed
    projectActions.clearProjectSuggestedTitle();

    goBack();
  }, [goBack, projectActions]);

  const renameProject = useUpdateProject({
    isDisplayedBeforeGoBack: true,
    onConfirm: () => {
      clearTitleAndGoBack();
    },
    onCancel: async () => {
      if (isBlogPostProjectType(projectType)) {
        clearFactsStorage();
      }
      await deleteProject();
      clearTitleAndGoBack();
    },
  });

  const handleDocumentGoBack = useCallback(() => {
    const projectName = pageActions.getProjectName();
    const isDefaultDocumentName = DEFAULT_DOCUMENT_NAME === projectName;
    const isNewProject = pageActions.isNewProject();

    if (isNewProject && isDefaultDocumentName) {
      const generatingMultiPrompt = pageActions.getGeneratingMultiPrompt();
      if (projectActions.isCurrentProjectEmpty() && !generatingMultiPrompt) {
        deleteProject().then(() => {
          clearTitleAndGoBack();
        });
      } else {
        renameProject();
      }

      return false;
    }

    // Clear the project's title only when it is about to be closed
    projectActions.clearProjectSuggestedTitle();

    return true;
  }, [pageActions, projectActions, deleteProject, clearTitleAndGoBack, renameProject]);

  const handleBlogGoBack = useCallback(() => {
    const isNewProject = pageActions.isNewProject();
    const currentProject = projectActions.getCurrentProject();

    if (isNewProject && currentProject.status === PROJECT_STATUS.blogDraft) {
      if (projectActions.isCurrentProjectEmpty()) {
        deleteProject().then(() => {
          clearTitleAndGoBack();
        });
      } else {
        renameProject();
      }
    } else {
      clearTitleAndGoBack();
    }
  }, [pageActions, projectActions, deleteProject, clearTitleAndGoBack, renameProject]);

  const handleLongFormGoBack = useCallback(() => {
    const isNewProject = pageActions.isNewProject();
    const currentProject = projectActions.getCurrentProject();

    if (isNewProject && currentProject.name === DEFAULT_DOCUMENT_NAME) {
      if (projectActions.isCurrentProjectEmpty()) {
        deleteProject().then(() => {
          clearTitleAndGoBack();
        });
      } else {
        renameProject();
      }
    } else {
      clearTitleAndGoBack();
    }
  }, [pageActions, projectActions, deleteProject, clearTitleAndGoBack, renameProject]);

  return useCallback(() => {
    const project = projectActions.getCurrentProject();
    if (!project) {
      return true;
    }

    if (isDocumentProjectType(project.type)) {
      return handleDocumentGoBack();
    } else if (isBlogPostProjectType(project.type)) {
      handleBlogGoBack();
      return false;
    } else if (isLongFormProjectType(project.type)) {
      handleLongFormGoBack();
      return false;
    }

    return true;
  }, [projectActions, handleDocumentGoBack, handleBlogGoBack, handleLongFormGoBack]);
};
